import {Injectable} from '@angular/core';
import * as moment from 'moment';
import {Observable, Subject} from 'rxjs';
import {Address, Appointment, Order, OrderProduct, Product, Store, TimeSlot, User} from '../../types/models';
import {DeliveryState, DeliveryType, OrderState, TimeSlotElementType, TimeSlotState} from '../../types/enums';
import {ShoppingListItem} from '../../types/io';

// configuration of a simulation, chosen on the simulator page
export interface DeliverySimulatorConfig {
  Orders: number;                     // appointments of the simulated time slot
  State: TimeSlotState;               // start state of the simulated time slot
  Delivered: number;                  // already finished orders (only for State DELIVERING)
  Employee: 'me' | 'other';           // who packs / delivers (only for State PACKING and DELIVERING)
  OpenDeliveryPayments: number;       // orders which are paid on delivery (payment still open)
  Notes: boolean;                     // customer notes on some orders
  UpcomingTimeSlots: number;          // further open time slots (following days) for the lists
}

export interface DeliverySimulatorLogEntry {
  Time: Date;
  Method: string;
  Path: string;
  Result: 'simulated' | 'blocked' | 'error';
  Message?: string;
}

// the answer of the fake backend: Handled = false lets the request through to the real api (only GET)
export interface DeliverySimulatorResponse {
  Handled: boolean;
  Body?: any;
  Error?: string;
}

interface DeliverySimulatorState {
  Active: boolean;
  Config: DeliverySimulatorConfig;
  Me: User;
  Store: Store;
  TimeSlots: TimeSlot[];
  Routes: { [timeSlotId: string]: string[] };
}

const STORAGE_KEY = 'kiwi-delivery-simulator';
const ID_PREFIX = 'sim-';

const FIRST_NAMES = ['Anna', 'Ben', 'Clara', 'David', 'Emma', 'Felix', 'Greta', 'Hannes', 'Ida', 'Jonas', 'Klara', 'Leon', 'Mia', 'Noah', 'Paula', 'Theo'];
const LAST_NAMES = ['Becker', 'Fischer', 'Hoffmann', 'Keller', 'Lange', 'Meyer', 'Neumann', 'Richter', 'Schmidt', 'Schulz', 'Wagner', 'Weber', 'Wolf', 'Zimmermann'];
const STREETS = ['Lindenstraße', 'Ahornweg', 'Birkenallee', 'Gartenstraße', 'Schulstraße', 'Mühlenweg', 'Bahnhofstraße', 'Kirchplatz', 'Rosenweg', 'Am Sportplatz'];
const NOTES = ['Bitte hinten klingeln.', 'Ware bitte beim Nachbarn (Nr. 5) abgeben.', 'Hund ist harmlos.', 'Bitte vor die Tür stellen.', '3. OG, kein Aufzug.'];
const PRODUCTS: { Name: string, Brand: string, Amount: number, Unit: string, PriceGross: number, Deposit?: number, Tax: number, Location: string, Quality?: string, Origin?: string }[] = [
  { Name: 'Bio Vollmilch 3,8%', Brand: 'Berchtesgadener Land', Amount: 1, Unit: 'l', PriceGross: 149, Tax: 7, Location: 'K-01' },
  { Name: 'Bergkäse am Stück', Brand: 'Allgäuer Hof', Amount: 200, Unit: 'g', PriceGross: 349, Tax: 7, Location: 'K-03' },
  { Name: 'Freilandeier', Brand: 'Gutshof', Amount: 10, Unit: 'Stk', PriceGross: 329, Tax: 7, Location: 'K-02' },
  { Name: 'Dinkelbrot', Brand: 'Bäckerei Kornfeld', Amount: 750, Unit: 'g', PriceGross: 389, Tax: 7, Location: 'B-01' },
  { Name: 'Äpfel Elstar', Brand: 'Obsthof am See', Amount: 1, Unit: 'kg', PriceGross: 279, Tax: 7, Location: 'O-01', Quality: 'Klasse I', Origin: 'Deutschland' },
  { Name: 'Bananen', Brand: 'Chiquita', Amount: 1, Unit: 'kg', PriceGross: 199, Tax: 7, Location: 'O-02', Quality: 'Klasse I', Origin: 'Kolumbien' },
  { Name: 'Kartoffeln festkochend', Brand: 'Heidegold', Amount: 2.5, Unit: 'kg', PriceGross: 399, Tax: 7, Location: 'O-05', Origin: 'Deutschland' },
  { Name: 'Rispentomaten', Brand: 'Gemüsegarten', Amount: 500, Unit: 'g', PriceGross: 249, Tax: 7, Location: 'O-03', Quality: 'Klasse I', Origin: 'Niederlande' },
  { Name: 'Spaghetti n.5', Brand: 'Barilla', Amount: 500, Unit: 'g', PriceGross: 129, Tax: 7, Location: 'T-02' },
  { Name: 'Passierte Tomaten', Brand: 'Mutti', Amount: 500, Unit: 'g', PriceGross: 99, Tax: 7, Location: 'T-03' },
  { Name: 'Haferflocken kernig', Brand: 'Kölln', Amount: 500, Unit: 'g', PriceGross: 119, Tax: 7, Location: 'T-01' },
  { Name: 'Mineralwasser still', Brand: 'Gerolsteiner', Amount: 0.75, Unit: 'l', PriceGross: 69, Deposit: 15, Tax: 19, Location: 'G-01' },
  { Name: 'Apfelschorle', Brand: 'Lift', Amount: 500, Unit: 'ml', PriceGross: 119, Deposit: 25, Tax: 19, Location: 'G-02' },
  { Name: 'Kaffee gemahlen', Brand: 'Dallmayr', Amount: 500, Unit: 'g', PriceGross: 699, Tax: 7, Location: 'T-05' },
  { Name: 'Spülmittel Zitrone', Brand: 'Pril', Amount: 500, Unit: 'ml', PriceGross: 189, Tax: 19, Location: 'D-01' },
  { Name: 'Toilettenpapier 3-lagig', Brand: 'Zewa', Amount: 8, Unit: 'Stk', PriceGross: 449, Tax: 19, Location: 'D-02' },
  { Name: 'Joghurt natur 3,5%', Brand: 'Weihenstephan', Amount: 500, Unit: 'g', PriceGross: 119, Tax: 7, Location: 'K-04' },
  { Name: 'Deutsche Markenbutter', Brand: 'Kerrygold', Amount: 250, Unit: 'g', PriceGross: 239, Tax: 7, Location: 'K-01' }
];

// simulates the backend of the delivery views (time slots of drivers) in the browser:
// no data reaches the database, no payment, push or mail is triggered
@Injectable({
  providedIn: 'root'
})
export class AdminDeliverySimulatorService {

  private state: DeliverySimulatorState = null;
  private log: DeliverySimulatorLogEntry[] = [];
  private changedSubject = new Subject<void>();

  constructor() {
    this.state = this.restore();
  }

  public static getDefaultConfig(): DeliverySimulatorConfig {
    return {
      Orders: 6,
      State: TimeSlotState.OPEN,
      Delivered: 0,
      Employee: 'me',
      OpenDeliveryPayments: 1,
      Notes: true,
      UpcomingTimeSlots: 2
    };
  }

  public get Changed(): Observable<void> {
    return this.changedSubject.asObservable();
  }

  isActive(): boolean {
    return !!this.state?.Active;
  }

  getConfig(): DeliverySimulatorConfig {
    return this.state?.Config ? { ...this.state.Config } : AdminDeliverySimulatorService.getDefaultConfig();
  }

  getTimeSlots(): TimeSlot[] {
    return this.clone(this.state?.TimeSlots || []);
  }

  getStore(): Store {
    return this.state?.Store;
  }

  getLog(): DeliverySimulatorLogEntry[] {
    return [...this.log];
  }

  start(config: DeliverySimulatorConfig, me: User, store: Store) {
    const meUser: User = { Id: me.Id, FirstName: me.FirstName, LastName: me.LastName, Mail: me.Mail, Phone: me.Phone };
    const timeSlots = [this.createTimeSlot(1, config, meUser, store, moment().add(30, 'minutes'), config.Orders, config.State)];
    for (let i = 0; i < (config.UpcomingTimeSlots || 0); i++) {
      const start = moment().add(i + 1, 'days').startOf('d').hours(i % 2 === 0 ? 10 : 15);
      timeSlots.push(this.createTimeSlot(i + 2, config, meUser, store, start, 3 + i * 2, TimeSlotState.OPEN));
    }
    const routes = {};
    timeSlots.forEach(timeSlot => routes[timeSlot.Id] = timeSlot.OrderAppointments.map(appointment => appointment.Id));
    this.state = { Active: true, Config: { ...config }, Me: meUser, Store: store, TimeSlots: timeSlots, Routes: routes };
    this.log = [];
    this.persist();
  }

  stop() {
    this.state = null;
    this.log = [];
    this.persist();
  }

  // simulates the payment of an order which is paid on delivery (the real payment needs a card terminal)
  markPaid(orderId: string) {
    const order = this.findOrder(orderId);
    if (order?.Payment) {
      order.Payment.Payed = true;
      order.Payment.PayedAt = new Date();
      this.persist();
    }
  }

  isSimulatedPath(path: string): boolean {
    return (path || '').indexOf(ID_PREFIX) !== -1;
  }

  handle(method: string, path: string, body?: any): DeliverySimulatorResponse {
    if (!this.isActive()) {
      return { Handled: false };
    }
    const cleanPath = (path || '').split('?')[0].replace(/^\/+/, '');
    let response: DeliverySimulatorResponse;
    try {
      response = method === 'GET' ? this.handleGet(cleanPath) : this.handleWrite(method, cleanPath, body);
    } catch (error) {
      response = { Handled: true, Error: typeof error === 'string' ? error : 'Simulation: unerwarteter Fehler' };
    }
    if (!response) {
      // writes on simulated objects which the simulator does not know are blocked, everything else reaches the real api
      response = method !== 'GET' && (this.isSimulatedPath(cleanPath) || this.isSimulatedPath(JSON.stringify(body || {})))
        ? { Handled: true, Error: 'Simulation: diese Aktion ist im Simulator nicht verfügbar' }
        : { Handled: false };
    }
    if (response.Handled) {
      this.addLog(method, cleanPath, response);
    }
    if (response.Handled && !response.Error && method !== 'GET') {
      this.persist();
    }
    return response;
  }

  /* --- request handling --- */

  private handleGet(path: string): DeliverySimulatorResponse {
    const routes: [RegExp, (match: RegExpMatchArray) => any][] = [
      [/^admin\/user\/task\/my$/, () => this.getMyTask()],
      [/^admin\/time-slot\/my$/, () => this.getMy()],
      [/^admin\/time-slot-group\/my$/, () => null],
      [/^admin\/time-slot\/open$/, () => this.getAllOpen().map(timeSlot => this.toView(timeSlot, false, false))],
      [/^admin\/time-slot\/open\/([^/]+)$/, match => this.getOpen(match[1])],
      [/^admin\/time-slot\/with-route\/([^/]+)$/, match => this.getWithRoute(match[1])],
      [/^admin\/time-slot\/order\/([^/]+)$/, match => this.clone(this.findOrder(match[1]))],
      [/^admin\/time-slot-element\/open$/, () => this.toElements(this.getAllOpen())],
      [/^admin\/time-slot-element\/open\/pending$/, () => this.toElements(this.getAllPending())],
      [/^admin\/time-slot\/(sim-[^/]+)$/, match => this.toView(this.findTimeSlot(match[1]), true, true)]
    ];
    for (const [regex, fct] of routes) {
      const match = path.match(regex);
      if (match) {
        return { Handled: true, Body: fct(match) };
      }
    }
    // requests for simulated objects never reach the real api
    if (this.isSimulatedPath(path)) {
      return { Handled: true, Body: null };
    }
    return null;
  }

  private handleWrite(method: string, path: string, body: any): DeliverySimulatorResponse {
    const routes: [RegExp, (match: RegExpMatchArray) => any][] = [
      [/^admin\/time-slot\/start\/packing\/([^/]+)$/, match => this.startPacking(match[1])],
      [/^admin\/time-slot\/abort\/packing\/([^/]+)$/, match => this.abortPacking(match[1])],
      [/^admin\/time-slot\/finish\/packing\/([^/]+)$/, match => this.finishPacking(match[1])],
      [/^admin\/time-slot\/start\/delivering\/appointment\/([^/]+)$/, match => this.startDeliveringAppointment(match[1])],
      [/^admin\/time-slot\/abort\/delivering\/appointment\/([^/]+)$/, match => this.abortDeliveringAppointment(match[1])],
      [/^admin\/time-slot\/finish\/delivering\/appointment\/([^/]+)$/, match => this.finishDeliveringAppointment(match[1])],
      [/^admin\/time-slot\/start\/delivering\/([^/]+)$/, match => this.startDelivering(match[1])],
      [/^admin\/time-slot\/abort\/delivering\/([^/]+)$/, match => this.abortDelivering(match[1])],
      [/^admin\/time-slot\/finish\/delivering\/([^/]+)$/, match => this.finishDelivering(match[1])],
      [/^admin\/time-slot\/finish\/appointment$/, () => this.finishAppointment(body)],
      [/^admin\/time-slot\/route\/([^/]+)$/, match => this.updateRoute(match[1], body)],
      [/^admin\/time-slot\/location\/([^/]+)$/, () => ({})],
      [/^admin\/user\/task\/location\/([^/]+)$/, () => ({})],
      [/^admin\/order\/book-corrections$/, () => this.bookOrder(body)],
      [/^admin\/order\/book-returns$/, () => this.bookOrder(body)],
      [/^admin\/order\/missing-product$/, () => this.missingProduct(body)],
      [/^admin\/order\/revert\/([^/]+)$/, match => this.revertOrder(match[1])]
    ];
    for (const [regex, fct] of routes) {
      const match = path.match(regex);
      if (match) {
        return { Handled: true, Body: fct(match) };
      }
    }
    return null;
  }

  /* --- get --- */

  private getMyTask() {
    const timeSlot = this.getMyTimeSlot();
    return { OrderId: null, TimeSlotId: timeSlot?.Id || null, TimeSlotGroupId: null };
  }

  private getMy(): TimeSlot {
    const timeSlot = this.getMyTimeSlot();
    if (!timeSlot) {
      return null;
    }
    const view = this.toView(timeSlot, true, true);
    delete view.OrderAppointments;
    return view;
  }

  private getOpen(id: string): TimeSlot {
    const timeSlot = this.findTimeSlot(id);
    if (!timeSlot || timeSlot.State === TimeSlotState.DONE) {
      return null;
    }
    return this.toView(timeSlot, true, true);
  }

  private getWithRoute(id: string): TimeSlot {
    const timeSlot = this.findTimeSlot(id);
    if (!timeSlot || timeSlot.State === TimeSlotState.DONE) {
      return null;
    }
    if (timeSlot.Employee && timeSlot.Employee.Id !== this.state.Me.Id) {
      throw 'Der Termin wird bereits von jemand anderes berarbeitet';
    }
    return this.toView(timeSlot, true, false);
  }

  private getAllOpen(): TimeSlot[] {
    return this.state.TimeSlots.filter(timeSlot => timeSlot.State === TimeSlotState.OPEN && moment(timeSlot.StartTime).isAfter(moment()));
  }

  private getAllPending(): TimeSlot[] {
    return this.state.TimeSlots.filter(timeSlot => timeSlot.State !== TimeSlotState.DONE);
  }

  /* --- time slot transitions (like time-slot.admin.pending.service of the api) --- */

  private getPending(id: string, employeeCheck: boolean, taskCheck: boolean, mustBeMine = false): TimeSlot {
    const timeSlot = this.findTimeSlot(id);
    if (!timeSlot || timeSlot.State === TimeSlotState.DONE || !timeSlot.OrderAppointments?.length) {
      throw 'Der Termin ist bereits abgeschlossen';
    }
    if (mustBeMine && timeSlot.Employee?.Id !== this.state.Me.Id) {
      throw 'Der Termin ist bereits abgeschlossen';
    }
    if (employeeCheck && timeSlot.Employee?.Id && timeSlot.Employee?.Id !== this.state.Me.Id) {
      throw 'Der Termin wird bereits von einem Mitarbeiter bearbeitet.';
    }
    if (taskCheck) {
      const myTimeSlot = this.getMyTimeSlot();
      if (myTimeSlot && myTimeSlot.Id !== timeSlot.Id) {
        throw 'Es kann nur eine Aufgabe gleichzeitig bearbeitet werden.';
      }
    }
    return timeSlot;
  }

  private startPacking(id: string): TimeSlot {
    const timeSlot = this.getPending(id, true, true);
    this.setEmployee(timeSlot, this.state.Me);
    timeSlot.State = TimeSlotState.PACKING;
    for (const order of this.getOrders(timeSlot)) {
      order.EmployeeId = this.state.Me.Id;
      this.updateDeliveryState(order, DeliveryState.PACKED, { EmployeeId: this.state.Me.Id });
    }
    return this.clone(timeSlot);
  }

  private abortPacking(id: string): TimeSlot {
    const timeSlot = this.getPending(id, false, false, true);
    this.setEmployee(timeSlot, null);
    timeSlot.State = TimeSlotState.OPEN;
    this.getOrders(timeSlot).forEach(order => order.EmployeeId = null);
    return this.clone(timeSlot);
  }

  private finishPacking(id: string): TimeSlot {
    const timeSlot = this.getPending(id, false, false, true);
    this.setEmployee(timeSlot, null);
    timeSlot.State = TimeSlotState.PACKED;
    for (const order of this.getOrders(timeSlot)) {
      order.EmployeeId = null;
      this.updateDeliveryState(order, DeliveryState.PACKED, { Done: true, EndTime: new Date() });
    }
    return this.clone(timeSlot);
  }

  private startDelivering(id: string): TimeSlot {
    const timeSlot = this.getPending(id, true, true);
    this.setEmployee(timeSlot, this.state.Me);
    timeSlot.State = TimeSlotState.DELIVERING;
    for (const order of this.getOrders(timeSlot).filter(order => order.State === OrderState.OPEN)) {
      order.EmployeeId = this.state.Me.Id;
      this.updateDeliveryState(order, DeliveryState.DELIVERING, { Done: true, StartTime: new Date(), EmployeeId: this.state.Me.Id });
    }
    return this.clone(timeSlot);
  }

  private abortDelivering(id: string): TimeSlot {
    const timeSlot = this.getPending(id, false, false, true);
    this.setEmployee(timeSlot, null);
    timeSlot.State = TimeSlotState.PACKED;
    for (const order of this.getOrders(timeSlot).filter(order => order.State === OrderState.OPEN && !!order.EmployeeId)) {
      order.EmployeeId = null;
      this.updateDeliveryState(order, DeliveryState.DELIVERING, { Done: false });
      this.updateDeliveryState(order, DeliveryState.DELIVERED, { Done: false });
    }
    return this.clone(timeSlot);
  }

  private finishDelivering(id: string): TimeSlot {
    const timeSlot = this.getPending(id, false, false, true);
    if (this.getOrders(timeSlot).find(order => order.State === OrderState.OPEN)) {
      throw 'Es gibt noch nicht abgeschlossene Aufträge zu dem Termin';
    }
    this.setEmployee(timeSlot, null);
    timeSlot.State = TimeSlotState.DONE;
    return this.clone(timeSlot);
  }

  private startDeliveringAppointment(appointmentId: string): TimeSlot {
    const { timeSlot, appointment } = this.findAppointment(appointmentId);
    this.getPending(timeSlot?.Id, false, false, true);
    if (this.getOrders(timeSlot).find(order => !!order.EmployeeId)) {
      throw 'Du kannst nicht mehr als eine Bestellung gleichzeitig ausliefern';
    }
    appointment.Order.EmployeeId = this.state.Me.Id;
    this.updateDeliveryState(appointment.Order, DeliveryState.DELIVERING, { Done: true, StartTime: new Date(), EmployeeId: this.state.Me.Id });
    return this.clone(timeSlot);
  }

  private abortDeliveringAppointment(appointmentId: string): TimeSlot {
    const { timeSlot, appointment } = this.findAppointment(appointmentId);
    this.getPending(timeSlot?.Id, false, false, true);
    appointment.Order.EmployeeId = null;
    return this.clone(timeSlot);
  }

  private finishDeliveringAppointment(appointmentId: string): TimeSlot {
    const { timeSlot, appointment } = this.findAppointment(appointmentId);
    this.getPending(timeSlot?.Id, false, false, true);
    this.updateDeliveryState(appointment.Order, DeliveryState.DELIVERING, { EndTime: new Date() });
    this.updateDeliveryState(appointment.Order, DeliveryState.DELIVERED, { Done: true, StartTime: new Date(), EmployeeId: this.state.Me.Id });
    return this.clone(timeSlot);
  }

  private finishAppointment(requestAppointment: Appointment): TimeSlot {
    const { timeSlot, appointment } = this.findAppointment(requestAppointment?.Id);
    this.getPending(timeSlot?.Id, false, false, true);
    this.applyAmounts(appointment.Order, requestAppointment.Order);
    this.updateDeliveryState(appointment.Order, DeliveryState.DELIVERED, { EndTime: new Date(), EmployeeId: this.state.Me.Id });
    appointment.Order.EmployeeId = null;
    appointment.Order.State = OrderState.DONE;
    return this.clone(timeSlot);
  }

  private updateRoute(id: string, requestTimeSlot: TimeSlot): string {
    const timeSlot = this.findTimeSlot(id);
    if (!timeSlot) {
      throw 'Der Termin ist bereits gelöscht';
    }
    if (timeSlot.State === TimeSlotState.DONE || (timeSlot.State === TimeSlotState.DELIVERING && timeSlot.Employee?.Id !== this.state.Me.Id)) {
      throw 'Die Route kann nicht mehr aktualisiert werden';
    }
    const ids = (requestTimeSlot?.Route || []).map(appointment => appointment.Id).filter(appointmentId => !!appointmentId);
    const known = timeSlot.OrderAppointments.map(appointment => appointment.Id);
    this.state.Routes[id] = [...ids.filter(appointmentId => known.includes(appointmentId)), ...known.filter(appointmentId => !ids.includes(appointmentId))];
    return 'ok';
  }

  private bookOrder(requestOrder: Order): string {
    const order = this.findOrder(requestOrder?.Id);
    if (!order) {
      throw 'Der Auftrag konnte nicht geladen werden';
    }
    this.applyAmounts(order, requestOrder);
    return 'ok';
  }

  // like order.admin.missing-product.service: remove the product or replace it by the scanned product (fictitious EAN)
  private missingProduct(request: { OrderId: string, ProductId: string, Code?: string }) {
    const order = this.findOrder(request?.OrderId);
    const orderProducts = (order?.OrderProducts || []).filter(item => item.Product?.Id === request.ProductId && item.Amount - (item.ReturnAmount || 0) > 0);
    if (!order || orderProducts.length === 0) {
      throw 'Das Produkt ist nicht (mehr) im Auftrag';
    }
    const normalize = (code: string) => (code || '').replace(/\D/g, '').replace(/^0+/, '');
    let replacement: Product = null;
    if (request.Code) {
      const source = PRODUCTS.find(product => normalize(this.createProduct(product).EAN) === normalize(request.Code));
      if (!source) {
        throw 'Kein Produkt mit dem Barcode ' + request.Code + ' gefunden (Simulation: EAN 4000000000001 bis ' + this.createProduct(PRODUCTS[PRODUCTS.length - 1]).EAN + ')';
      }
      replacement = this.createProduct(source);
      if (replacement.Id === request.ProductId) {
        throw 'Das ist das fehlende Produkt. Bitte ein anderes Produkt scannen.';
      }
    }
    const paid = !!order.Payment?.Payed;
    for (const orderProduct of orderProducts) {
      const amount = orderProduct.Amount - (orderProduct.ReturnAmount || 0);
      const line = replacement ? `Ersetzt: ${amount}x ${orderProduct.Name} durch ${replacement.Name}` : `Fehlt: ${amount}x ${orderProduct.Name} (entfernt)`;
      order.Note = order.Note ? `${order.Note} | ${line}` : line;
      if (paid) {
        orderProduct.ReturnAmount = orderProduct.Amount;
      } else {
        orderProduct.Amount = orderProduct.ReturnAmount || 0;
      }
      if (replacement) {
        const source = PRODUCTS.find(product => product.Name === replacement.Name);
        order.OrderProducts.push({
          Id: `${orderProduct.Id}-replacement`, Name: replacement.Name, Amount: amount, InitialAmount: amount, ReturnAmount: 0,
          PriceGross: Math.min(source.PriceGross, orderProduct.PriceGross), Deposit: source.Deposit || 0, Tax: source.Tax, OrderValue: true,
          Product: replacement
        } as OrderProduct);
      }
    }
    return { Removed: true, Replacement: replacement ? { ProductId: replacement.Id, Name: replacement.Name, EAN: replacement.EAN } : null };
  }

  private revertOrder(orderId: string): Order {
    for (const timeSlot of this.state.TimeSlots) {
      const appointment = timeSlot.OrderAppointments.find(item => item.Order?.Id === orderId);
      if (appointment) {
        timeSlot.OrderAppointments = timeSlot.OrderAppointments.filter(item => item !== appointment);
        this.state.Routes[timeSlot.Id] = (this.state.Routes[timeSlot.Id] || []).filter(id => id !== appointment.Id);
        return this.clone(appointment.Order);
      }
    }
    throw 'Der Auftrag konnte nicht geladen werden';
  }

  /* --- helper --- */

  private getMyTimeSlot(): TimeSlot {
    return this.state.TimeSlots.find(timeSlot => timeSlot.Employee?.Id === this.state.Me.Id && timeSlot.State !== TimeSlotState.DONE);
  }

  private findTimeSlot(id: string): TimeSlot {
    return this.state.TimeSlots.find(timeSlot => timeSlot.Id === id);
  }

  private findAppointment(appointmentId: string): { timeSlot: TimeSlot, appointment: Appointment } {
    for (const timeSlot of this.state.TimeSlots) {
      const appointment = timeSlot.OrderAppointments.find(item => item.Id === appointmentId);
      if (appointment) {
        return { timeSlot, appointment };
      }
    }
    throw 'Der Termin ist bereits abgeschlossen';
  }

  private findOrder(orderId: string): Order {
    for (const timeSlot of this.state?.TimeSlots || []) {
      const appointment = timeSlot.OrderAppointments.find(item => item.Order?.Id === orderId);
      if (appointment) {
        return appointment.Order;
      }
    }
    return null;
  }

  private getOrders(timeSlot: TimeSlot): Order[] {
    return (timeSlot?.OrderAppointments || []).map(appointment => appointment.Order);
  }

  private setEmployee(timeSlot: TimeSlot, user: User) {
    timeSlot.Employee = user ? { ...user } : null;
  }

  private updateDeliveryState(order: Order, key: DeliveryState, values: any) {
    const state = (order.DeliveryState || []).find(item => item.Key === key);
    if (state) {
      Object.assign(state, values);
    }
  }

  private applyAmounts(order: Order, requestOrder: Order) {
    if (!order || !requestOrder) {
      return;
    }
    for (const requestProduct of requestOrder.OrderProducts || []) {
      const orderProduct = (order.OrderProducts || []).find(item => item.Id === requestProduct.Id);
      if (orderProduct) {
        orderProduct.Amount = requestProduct.Amount;
        orderProduct.ReturnAmount = requestProduct.ReturnAmount;
      }
    }
    if (requestOrder.DepositReturns) {
      order.DepositReturns = this.clone(requestOrder.DepositReturns);
    }
  }

  // builds the response like the api does (Store, Route, ShoppingList, Notes, EstimatedDeliveryTime)
  private toView(timeSlot: TimeSlot, loadRoute: boolean, loadShoppingList: boolean): TimeSlot {
    if (!timeSlot) {
      return null;
    }
    const view: TimeSlot = this.clone(timeSlot);
    const store = this.state.Store;
    (view as any).Store = { Id: store?.Id, AddressId: store?.AddressId, Address: store?.Address };
    const routeIds = this.state.Routes[timeSlot.Id] || [];
    const route = [...view.OrderAppointments].sort((a, b) => routeIds.indexOf(a.Id) - routeIds.indexOf(b.Id));
    this.setEstimatedDeliveryTime(view, route);
    if (loadRoute) {
      view.Route = this.clone(route);
    }
    if (loadShoppingList) {
      view.ShoppingList = this.getShoppingList(view.OrderAppointments);
      view.Notes = view.OrderAppointments
        .filter(appointment => !!appointment.Order?.Note)
        .map(appointment => ({ Box: appointment.Box, User: appointment.Order.User, Note: appointment.Order.Note }));
    }
    return view;
  }

  private setEstimatedDeliveryTime(timeSlot: TimeSlot, route: Appointment[]) {
    const start = moment(timeSlot.StartTime);
    const duration = moment(timeSlot.EndTime).diff(start, 'minutes');
    const step = route.length > 0 ? Math.floor(duration / route.length) : 0;
    route.forEach((appointment, index) => {
      const estimated = moment(start).add(step * index + Math.floor(step / 2), 'minutes').toDate();
      appointment.EstimatedDeliveryTime = estimated;
      const original = timeSlot.OrderAppointments.find(item => item.Id === appointment.Id);
      if (original) {
        original.EstimatedDeliveryTime = estimated;
      }
    });
  }

  private getShoppingList(appointments: Appointment[]): ShoppingListItem[] {
    const items: { [productId: string]: ShoppingListItem } = {};
    for (const appointment of appointments) {
      for (const orderProduct of appointment.Order?.OrderProducts || []) {
        const productId = orderProduct.Product.Id;
        if (!items[productId]) {
          items[productId] = { Product: orderProduct.Product, Amount: 0, Appointments: [], Inventory: orderProduct.Product.Inventory };
        }
        const amount = orderProduct.Amount - (orderProduct.ReturnAmount || 0);
        items[productId].Amount += amount;
        items[productId].Appointments.push({
          Id: appointment.Id,
          Box: appointment.Box,
          Amount: amount,
          User: appointment.Order.User,
          Address: appointment.Order.Address,
          Note: appointment.Order.Note,
          OrderId: appointment.Order.Id
        });
      }
    }
    return Object.values(items).sort((a, b) => (a.Inventory?.StoreLocation || '').localeCompare(b.Inventory?.StoreLocation || ''));
  }

  private toElements(timeSlots: TimeSlot[]) {
    return timeSlots
      .map(timeSlot => ({ Type: TimeSlotElementType.TIME_SLOT, StartTime: timeSlot.StartTime, TimeSlot: this.toView(timeSlot, false, false) }))
      .sort((a, b) => moment(a.StartTime).diff(moment(b.StartTime)));
  }

  /* --- fictitious data --- */

  private createTimeSlot(index: number, config: DeliverySimulatorConfig, me: User, store: Store, start: moment.Moment, orderCount: number, state: TimeSlotState): TimeSlot {
    const id = `${ID_PREFIX}time-slot-${index}`;
    const colleague: User = { Id: `${ID_PREFIX}user-colleague`, FirstName: 'Max', LastName: 'Kollege', Mail: 'max.kollege@example.com', Phone: '0151 0000000' };
    const inProgress = state === TimeSlotState.PACKING || state === TimeSlotState.DELIVERING;
    const employee = inProgress ? (config.Employee === 'other' ? colleague : me) : null;
    const timeSlot: TimeSlot = {
      Id: id,
      Name: index === 1 ? 'Simulation' : `Simulation ${index}`,
      StoreId: store?.Id,
      State: state,
      Draft: false,
      Terminal: false,
      Appointments: Math.max(orderCount, 10),
      OrderTime: moment(start).subtract(3, 'hours').toDate(),
      PackingTime: moment(start).subtract(1, 'hours').toDate(),
      StartTime: start.toDate(),
      EndTime: moment(start).add(3, 'hours').toDate(),
      Employee: employee,
      OrderAppointments: []
    };
    const random = this.random(index * 7919);
    for (let i = 0; i < orderCount; i++) {
      const order = this.createOrder(index, i, store, random, config, i < (index === 1 ? config.OpenDeliveryPayments : 0));
      this.applyStartState(order, state, employee, index === 1 && state === TimeSlotState.DELIVERING && i < config.Delivered);
      timeSlot.OrderAppointments.push({
        Id: `${ID_PREFIX}appointment-${index}-${i + 1}`,
        Box: `${i + 1}`,
        Number: i + 1,
        OrderId: order.Id,
        TimeSlotId: id,
        Order: order
      });
    }
    return timeSlot;
  }

  private applyStartState(order: Order, state: TimeSlotState, employee: User, delivered: boolean) {
    const now = new Date();
    if (state === TimeSlotState.PACKING) {
      order.EmployeeId = employee?.Id;
      this.updateDeliveryState(order, DeliveryState.PACKED, { EmployeeId: employee?.Id });
    }
    if (state === TimeSlotState.PACKED || state === TimeSlotState.DELIVERING) {
      this.updateDeliveryState(order, DeliveryState.PACKED, { Done: true, EndTime: now });
    }
    if (state === TimeSlotState.DELIVERING) {
      this.updateDeliveryState(order, DeliveryState.DELIVERING, { Done: true, StartTime: now, EmployeeId: employee?.Id });
      if (delivered) {
        this.updateDeliveryState(order, DeliveryState.DELIVERED, { Done: true, StartTime: now, EndTime: now, EmployeeId: employee?.Id });
        order.State = OrderState.DONE;
        order.EmployeeId = null;
        if (order.Payment) {
          order.Payment.Payed = true;
        }
      } else {
        order.EmployeeId = employee?.Id;
      }
    }
  }

  private createOrder(timeSlotIndex: number, index: number, store: Store, random: () => number, config: DeliverySimulatorConfig, openDeliveryPayment: boolean): Order {
    const id = `${ID_PREFIX}order-${timeSlotIndex}-${index + 1}`;
    const pick = <T>(items: T[]): T => items[Math.floor(random() * items.length)];
    const user: User = {
      Id: `${ID_PREFIX}user-${timeSlotIndex}-${index + 1}`,
      FirstName: pick(FIRST_NAMES),
      LastName: pick(LAST_NAMES),
      Phone: `0170 ${Math.floor(1000000 + random() * 8999999)}`
    };
    const address = this.createAddress(id, store, random, pick);
    const products = [...PRODUCTS].sort(() => random() - 0.5).slice(0, 3 + Math.floor(random() * 6));
    const orderProducts: OrderProduct[] = products.map((product, productIndex) => {
      const amount = 1 + Math.floor(random() * 3);
      return {
        Id: `${id}-product-${productIndex + 1}`,
        Name: product.Name,
        Amount: amount,
        InitialAmount: amount,
        ReturnAmount: 0,
        PriceGross: product.PriceGross,
        Deposit: product.Deposit || 0,
        Tax: product.Tax,
        OrderValue: true,
        Product: this.createProduct(product)
      };
    });
    const price = orderProducts.reduce((sum, orderProduct) => sum + orderProduct.Amount * (orderProduct.PriceGross + orderProduct.Deposit), 0);
    const deliveryCost = 290;
    return {
      Id: id,
      UserId: user.Id,
      User: user,
      StoreId: store?.Id,
      AddressId: address.Id,
      Address: address,
      State: OrderState.OPEN,
      DeliveryType: DeliveryType.APPOINTMENT,
      DeliveryState: [
        { Key: DeliveryState.INCOMING, Done: true },
        { Key: DeliveryState.PACKED, Done: false },
        { Key: DeliveryState.DELIVERING, Done: false },
        { Key: DeliveryState.LOCATION },
        { Key: DeliveryState.DELIVERED, Done: false }
      ],
      EmployeeId: null,
      Note: config.Notes && random() < 0.35 ? pick(NOTES) : null,
      Price: price,
      Total: price + deliveryCost,
      DeliveryCost: deliveryCost,
      Tip: 0,
      OrderProducts: orderProducts,
      DepositReturns: [],
      Orders: [],
      Payment: openDeliveryPayment
        ? {
          Id: `${id}-payment`, PaymentMethod: 'ec-delivery', Payed: false, PromisePayed: true,
          Method: { Id: `${ID_PREFIX}payment-method-ec-delivery`, Name: 'ec-delivery', Type: 'ec-delivery', DeliveryPayment: true }
        }
        : {
          Id: `${id}-payment`, PaymentMethod: 'paypal', Payed: true, PromisePayed: false, PayedAt: new Date(),
          Method: { Id: `${ID_PREFIX}payment-method-paypal`, Name: 'paypal', Type: 'paypal', DeliveryPayment: false }
        },
      CreatedAt: moment().subtract(1, 'days').toDate()
    } as Order;
  }

  private createAddress(id: string, store: Store, random: () => number, pick: <T>(items: T[]) => T): Address {
    // around the store (about 3 km), so the map and the navigation show a realistic route
    const latitude = store?.Address?.Latitude ?? 52.52;
    const longitude = store?.Address?.Longitude ?? 13.405;
    return {
      Id: `${id}-address`,
      Street: `${pick(STREETS)} ${1 + Math.floor(random() * 60)}`,
      Zip: store?.Address?.Zip || '10115',
      City: store?.Address?.City || 'Berlin',
      Latitude: latitude + (random() - 0.5) * 0.05,
      Longitude: longitude + (random() - 0.5) * 0.08
    };
  }

  private createProduct(product: typeof PRODUCTS[number]): Product {
    const index = PRODUCTS.findIndex(item => item.Name === product.Name);
    return {
      Id: `${ID_PREFIX}product-${index + 1}`,
      Name: product.Name,
      Brand: product.Brand,
      Amount: product.Amount,
      Unit: product.Unit,
      Quality: product.Quality,
      Origin: product.Origin,
      EAN: `40000000${String(index + 1).padStart(5, '0')}`,
      Inventory: { StoreLocation: product.Location }
    } as Product;
  }

  // deterministic random numbers, so the same configuration always creates the same data
  private random(seed: number): () => number {
    let value = seed % 2147483647 || 1;
    return () => {
      value = (value * 16807) % 2147483647;
      return (value - 1) / 2147483646;
    };
  }

  private addLog(method: string, path: string, response: DeliverySimulatorResponse) {
    const result = response.Error ? (response.Error.startsWith('Simulation:') ? 'blocked' : 'error') : 'simulated';
    this.log.unshift({ Time: new Date(), Method: method, Path: path, Result: result, Message: response.Error });
    this.log = this.log.slice(0, 100);
    this.changedSubject.next();
  }

  private clone<T>(object: T): T {
    return object == null ? object : JSON.parse(JSON.stringify(object));
  }

  private persist() {
    try {
      if (this.state) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // without session storage the simulation only lives until the next reload
    }
    this.changedSubject.next();
  }

  private restore(): DeliverySimulatorState {
    try {
      const value = sessionStorage.getItem(STORAGE_KEY);
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  }
}
